/**
 * Taxonomy Service
 *
 * API service for category taxonomy and tag operations.
 * Communicates with the CategoryTaxonomy module backend.
 *
 * MVP 1.3 Phase 2: Search Taxonomy & SEO
 *
 * Location: apps/web/src/services/taxonomyService.js
 */

import apiRequest from './api';

/**
 * Get complete taxonomy (categories + tags)
 * @param {Object} options - Query options
 * @param {boolean} options.includeCounts - Include experience counts (default true)
 * @returns {Promise<Object>} { categories, tags, metadata }
 */
export async function getCompleteTaxonomy({ includeCounts = true } = {}) {
  const params = new URLSearchParams();
  if (includeCounts !== undefined) {
    params.append('includeCounts', includeCounts);
  }

  const response = await apiRequest(`/api/v1/taxonomy?${params.toString()}`, {
    method: 'GET'
  });
  return response.data;
}

/**
 * Get category hierarchy for specific L1 category
 * @param {string} slug - L1 category slug
 * @param {Object} options - Query options
 * @param {boolean} options.includeCounts - Include experience counts
 * @returns {Promise<Object>} Category hierarchy
 */
export async function getCategoryHierarchy(slug, { includeCounts = true } = {}) {
  const params = new URLSearchParams();
  if (includeCounts !== undefined) {
    params.append('includeCounts', includeCounts);
  }

  const response = await apiRequest(`/api/v1/taxonomy/categories/${slug}?${params.toString()}`, {
    method: 'GET'
  });
  return response.data;
}

/**
 * Get all L1 categories
 * @returns {Promise<Array>} Array of L1 categories
 */
export async function getAllL1Categories() {
  const response = await apiRequest('/api/v1/taxonomy/categories', {
    method: 'GET'
  });
  return response.data;
}

/**
 * Get L2 categories (optionally filtered by parent)
 * @param {number} parentId - Parent L1 category ID (optional)
 * @returns {Promise<Array>} Array of L2 categories
 */
export async function getL2Categories(parentId = null) {
  const params = new URLSearchParams();
  if (parentId) {
    params.append('parentId', parentId);
  }

  const response = await apiRequest(`/api/v1/taxonomy/categories/l2?${params.toString()}`, {
    method: 'GET'
  });
  return response.data;
}

/**
 * Get L3 categories (optionally filtered by parent)
 * @param {number} parentId - Parent L2 category ID (optional)
 * @returns {Promise<Array>} Array of L3 categories
 */
export async function getL3Categories(parentId = null) {
  const params = new URLSearchParams();
  if (parentId) {
    params.append('parentId', parentId);
  }

  const response = await apiRequest(`/api/v1/taxonomy/categories/l3?${params.toString()}`, {
    method: 'GET'
  });
  return response.data;
}

/**
 * Search categories by name
 * @param {string} query - Search query (minimum 2 characters)
 * @param {number} limit - Maximum results (default 10)
 * @returns {Promise<Array>} Array of matching categories
 */
export async function searchCategories(query, limit = 10) {
  const params = new URLSearchParams({
    q: query,
    limit: limit.toString()
  });

  const response = await apiRequest(`/api/v1/taxonomy/categories/search?${params.toString()}`, {
    method: 'GET'
  });
  return response.data;
}

/**
 * Get all tags (grouped by category)
 * @param {Object} options - Query options
 * @param {string} options.category - Filter by tag category (optional)
 * @param {boolean} options.includeCounts - Include experience counts
 * @returns {Promise<Object>} Tags grouped by category
 */
export async function getAllTags({ category = null, includeCounts = true } = {}) {
  const params = new URLSearchParams();
  if (category) {
    params.append('category', category);
  }
  if (includeCounts !== undefined) {
    params.append('includeCounts', includeCounts);
  }

  const response = await apiRequest(`/api/v1/taxonomy/tags?${params.toString()}`, {
    method: 'GET'
  });
  return response.data;
}

/**
 * Search tags by name
 * @param {string} query - Search query (minimum 2 characters)
 * @param {number} limit - Maximum results (default 10)
 * @returns {Promise<Array>} Array of matching tags
 */
export async function searchTags(query, limit = 10) {
  const params = new URLSearchParams({
    q: query,
    limit: limit.toString()
  });

  const response = await apiRequest(`/api/v1/taxonomy/tags/search?${params.toString()}`, {
    method: 'GET'
  });
  return response.data;
}

/**
 * Get experience taxonomy (categories + tags for a specific experience)
 * @param {string} experienceId - Experience UUID
 * @returns {Promise<Object>} { experience_id, categories, tags, primary_category }
 */
export async function getExperienceTaxonomy(experienceId) {
  const response = await apiRequest(`/api/v1/taxonomy/experiences/${experienceId}`, {
    method: 'GET'
  });
  return response.data;
}

/**
 * Update experience taxonomy
 * @param {string} experienceId - Experience UUID
 * @param {Object} taxonomy - Taxonomy update
 * @param {Array} taxonomy.categories - Category assignments
 * @param {Array} taxonomy.tagIds - Tag IDs
 * @returns {Promise<Object>} Updated taxonomy
 */
export async function updateExperienceTaxonomy(experienceId, taxonomy) {
  const response = await apiRequest(`/api/v1/taxonomy/experiences/${experienceId}`, {
    method: 'PUT',
    body: JSON.stringify(taxonomy)
  });
  return response.data;
}

/**
 * Get experiences by category
 * @param {Object} categoryIds - Category IDs
 * @param {number} categoryIds.l1Id - L1 category ID
 * @param {number} categoryIds.l2Id - L2 category ID
 * @param {number} categoryIds.l3Id - L3 category ID
 * @param {boolean} primaryOnly - Filter by primary category only
 * @returns {Promise<Object>} { experience_ids, count }
 */
export async function getExperiencesByCategory({ l1Id, l2Id, l3Id }, primaryOnly = false) {
  const params = new URLSearchParams();
  if (l1Id) {params.append('l1Id', l1Id);}
  if (l2Id) {params.append('l2Id', l2Id);}
  if (l3Id) {params.append('l3Id', l3Id);}
  if (primaryOnly) {params.append('primaryOnly', 'true');}

  const response = await apiRequest(`/api/v1/taxonomy/experiences/by-category?${params.toString()}`, {
    method: 'GET'
  });
  return response.data;
}

/**
 * Get experiences by tags
 * @param {Array<number>} tagIds - Tag IDs
 * @param {string} mode - 'union' (OR) or 'intersection' (AND)
 * @returns {Promise<Object>} { experience_ids, count, mode }
 */
export async function getExperiencesByTags(tagIds, mode = 'union') {
  const params = new URLSearchParams({
    tagIds: tagIds.join(','),
    mode
  });

  const response = await apiRequest(`/api/v1/taxonomy/experiences/by-tags?${params.toString()}`, {
    method: 'GET'
  });
  return response.data;
}

export default {
  getCompleteTaxonomy,
  getCategoryHierarchy,
  getAllL1Categories,
  getL2Categories,
  getL3Categories,
  searchCategories,
  getAllTags,
  searchTags,
  getExperienceTaxonomy,
  updateExperienceTaxonomy,
  getExperiencesByCategory,
  getExperiencesByTags
};
